import { GlobalContext, IModalFormElement } from "components/GlobalContext";
import { createFilterableArrayColumnDefinition, createFilterableNumberRangeColumnDefinition, IColumnDefinition } from "components/SortableTable";
import IFireDepartment, { IFireDepartmentCreateOrUpdate, IFireDepartmentFilters, IFireDepartmentInitResult } from "models/sql/IFireDepartment";
import React from "react";
import { useContext, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import FireDepartmentService from "services/FireDepartmentService";
import { addNewLogEntry, applyGlobalFilterData, generateLog, getEnvironment } from "utilities/MiscUtilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressBook, faHome, faLink, faListCheck, faMoneyCheck } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import IEquipment from "models/sql/IEquipment";
import CommonLookupDisplay from "components/CommonLookupDisplay";
import ContactViewer from "components/ContactViewer";

interface IFireDepartmentsLookupProps {
    baseData?: IFireDepartmentInitResult;
}

const COUNTRIES_FILTER_KEY = "hqcountries";
const STATES_FILTER_KEY = "hqstates";

const FireDepartmentsLookup = (props: IFireDepartmentsLookupProps) => {
    const [filter, setFilter] = useState<IFireDepartmentFilters>({});
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [fireDepartments, setFireDepartments] = useState<IFireDepartment[]>([]);
    const navigate = useNavigate();
    const [forceRefresh, setForceRefresh] = useState<Date>(new Date());
    const priorities = ["Target", "Contact Made", "Follow-Up Need", "Rep Hand-Off", "Active"];

    const saveEntryUpdate = async (entry: IFireDepartment, allData: IFireDepartment[]) => {
        globalContext.setLoading(true);
        const accessToken = await globalContext.getAccessTokenAsync();
        const data: IFireDepartmentCreateOrUpdate = {
            fireDepartmentId: entry.fireDepartmentId,
            dealerId: entry.dealerId,
            activeFirefightersCareer: entry.activeFirefightersCareer,
            activeFirefightersPaidPerCall: entry.activeFirefightersPaidPerCall,
            activeFirefightersVolunteer: entry.activeFirefightersVolunteer,
            county: entry.county,
            deptType: entry.deptType,
            fdid: entry.fdid,
            fireDeptName: entry.fireDeptName,
            hqaddr1: entry.hqaddr1,
            hqaddr2: entry.hqaddr2,
            hqcity: entry.hqcity,
            hqCountry: entry.hqCountry,
            hqfax: entry.hqfax,
            hqphone: entry.hqphone,
            hqstate: entry.hqstate,
            hqzip: entry.hqzip,
            mailAddr1: entry.mailAddr1,
            mailAddr2: entry.mailAddr2,
            mailCity: entry.mailCity,
            mailPobox: entry.mailPobox,
            mailState: entry.mailState,
            mailZip: entry.mailZip,
            nonFirefightingCivilian: entry.nonFirefightingCivilian,
            nonFirefightingVolunteer: entry.nonFirefightingVolunteer,
            numberOfStations: entry.numberOfStations,
            organizationType: entry.organizationType,
            primaryAgencyForEmergencyMgmt: entry.primaryAgencyForEmergencyMgmt,
            website: entry.website,
            priority: entry.priority,
            status: entry.status,
            customerOf: entry.customerOf,
            action: entry.action,
            notes: entry.notes,
            mailLongitude: entry.mailLongitude,
            mailLatitude: entry.mailLatitude,
            hqlongitude: entry.hqlongitude,
            hqlatitude: entry.hqlatitude,
            systemDataJson: entry.systemDataJson,
            emailSuffix: entry.emailSuffix,
            dashboardEmails: entry.dashboardEmails,
            defaultRoles: entry.defaultRoles,
            accessRequests: entry.accessRequests,
            lastAccessRequest: entry.lastAccessRequest,
            code: entry.code,
            facebookUrl: entry.facebookUrl,
            instagramUrl: entry.instagramUrl,
            tiktokUrl: entry.tiktokUrl,
            youtubeUrl: entry.youtubeUrl,
            twitterUrl: entry.twitterUrl,
            population: entry.population,
            specializations: entry.specializations,
            finance: entry.finance,
            lastUpdated: entry.lastUpdated,
            lastUpdatedBy: entry.lastUpdatedBy
        };

        await FireDepartmentService.CreateOrUpdateAsync(accessToken, data);
        allData[allData.findIndex(x => x.fireDepartmentId === data.fireDepartmentId)] = data;
        setForceRefresh(new Date());
        globalContext.setLoading(false);
    };

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        FireDepartmentService.DownloadExcelFileAsync(
            accessToken,
            "FiredepartmentExport.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const showQuickNotes = (entry: IFireDepartment, allData: IFireDepartment[]) => {
        const formData: IModalFormElement[] = [{
            key: "Notes",
            type: "textarea",
            title: "Notes",
            value: entry.notes,
            style: { width: "100%", height: "200px" }
        }];

        globalContext.showModal({
            title: `${entry.fireDeptName} quick notes view`,
            cancelButtonTitle: "Close",
            formElements: formData,
            content: <></>,
            size: "lg",
            okButtonTitle: "Save Changes",
            okCallback: (data: any) => {
                let updateEntry = fireDepartments.find(e => e.fireDepartmentId === entry.fireDepartmentId);
                if (updateEntry) {
                    // @ts-ignore
                    updateEntry.notes = data[0].value;

                    updateEntry =
                        addNewLogEntry<IFireDepartment>(
                            (new Date()),
                            `${globalContext.userName} updated notes`,
                            globalContext.userEmail,
                            101,
                            updateEntry);

                    saveEntryUpdate(updateEntry, allData);
                    setFireDepartments([...fireDepartments]);
                }
            }
        });
    };

    const showQuickLog = (entry: IFireDepartment) => {
        globalContext.showModal({
            title: `${entry.fireDeptName} quick log view`,
            cancelButtonTitle: "Close",
            content: generateLog<IFireDepartment>(entry),
            size: "lg"
        })
    };

    const showContacts = (entry: IFireDepartment) => {
        globalContext.showModal({
            title: `${entry.fireDeptName} contacts`,
            cancelButtonTitle: "Close",
            content: <ContactViewer
                source="FireDepartment"
                defaultOpen={true}
                entityId={entry.fireDepartmentId}
                service={FireDepartmentService}
                canEdit={globalContext.roles.some(r => r === "Web.Contacts")} />,
            size: "lg"
        })
    }

    const getColumns = (): IColumnDefinition<IFireDepartment, IFireDepartmentFilters>[] => {
        return [
            createFilterableArrayColumnDefinition<IFireDepartment, IFireDepartmentFilters>(
                globalContext,
                "fireDeptName",
                "fireDeptNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`fireDeptName${row.fireDepartmentId}`}>
                    {
                        (row.fireDeptName || "").split(",")
                            .map(
                                (e, index, array) => <React.Fragment key={index}>{e}{array.length > 1 && <br></br>}</React.Fragment>)
                    }</td>),
            {
                propName: "fireDeptName",
                title: "Actions",
                customRenderer: (value, data, allData) => {
                    return (
                        <td key={`align-top actions${value}`}>
                            {globalContext.roles.some(r => r === "Web.Admin") ?
                                <>
                                    <span
                                        className="quick-action me-1"
                                        title="Quick note view"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            showQuickNotes(data, allData);
                                            return false;
                                        }}><FontAwesomeIcon icon={faMoneyCheck as IconProp} /></span>
                                    <span
                                        className="quick-action me-1"
                                        title="Activity log"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            showQuickLog(data);
                                            return false;
                                        }}><FontAwesomeIcon icon={faListCheck as IconProp} /></span>
                                    <span
                                        className="quick-action me-1"
                                        title="Add new station"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            window.open(`/DPS/Station/Detail/?defs={"fireDepartmentId":${data.fireDepartmentId}}`);
                                        }}><FontAwesomeIcon icon={faHome as IconProp} /></span>
                                    <span
                                        className="quick-action"
                                        title="Add new contact"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            showContacts(data)
                                        }}><FontAwesomeIcon
                                            icon={faAddressBook as IconProp}
                                            style={{
                                                color: data.contactLinks && data.contactLinks.length > 0 ? "#198754" : "#ccc"
                                            }} /></span>
                                    <span
                                        className="quick-action"
                                        title="Department Url"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            if (data.website) {
                                                window.open(data.website || "");
                                            }
                                        }}><FontAwesomeIcon
                                            icon={faLink as IconProp}
                                            style={{
                                                color: data.website ? "#198754" : "#ccc"
                                            }} /></span>
                                </> : <></>
                            }
                        </td>)
                },
                sortable: false
            },
            createFilterableArrayColumnDefinition<IFireDepartment, IFireDepartmentFilters>(
                globalContext,
                "priority",
                "priorities",
                true,
                filter,
                undefined,
                (value, row, allData) => <td className="align-top" key={`priority${row.fireDepartmentId}`}>
                    {
                        globalContext.roles.some(r => r === "Web.Admin") &&
                        <Form.Select
                            style={{ width: "150px" }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                            }}
                            onChange={async (e) => {
                                // @ts-ignore
                                await saveEntryUpdate(
                                    {
                                        ...addNewLogEntry(
                                            (new Date()),
                                            `${globalContext.userName} updated priority to '${e.currentTarget.value}'`,
                                            globalContext.userEmail,
                                            101,
                                            row),
                                        priority: e.currentTarget.value
                                    },
                                    allData);

                                setForceRefresh(new Date());
                            }}
                            value={value}>
                            <option key={"empty"} value={""}>-</option>
                            {
                                priorities.map((s, index) => <option key={index} value={s}>{s}</option>)
                            }
                        </Form.Select>
                    }
                    {
                        !globalContext.roles.some(r => r === "Web.Admin") && <>{value}</>
                    }
                </td>,
                "Outbound"),
            createFilterableArrayColumnDefinition<IFireDepartment, IFireDepartmentFilters>(
                globalContext,
                "hqstate",
                "hqstates",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`hqState${row.fireDepartmentId}`}>
                    {
                        (row.hqstate || "").split(",")
                            .map(
                                (e, index, array) => <React.Fragment key={index}>{e}{array.length > 1 && <br></br>}</React.Fragment>)
                    }</td>,
                "State"),
            createFilterableArrayColumnDefinition<IFireDepartment, IFireDepartmentFilters>(
                globalContext,
                "hqCountry",
                "hqcountries",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`hqcountries${row.fireDepartmentId}`}>
                    {
                        (row.hqCountry || "").split(",")
                            .map(
                                (e, index, array) => <React.Fragment key={index}>{e}{array.length > 1 && <br></br>}</React.Fragment>)
                    }</td>,
                "Country"),
            createFilterableNumberRangeColumnDefinition<IFireDepartment, IFireDepartmentFilters>(
                globalContext,
                "numberOfStations",
                // @ts-ignore
                "numberOfStations",
                true,
                filter,
                (value: any, row: any) => <td className="align-top" key={`numberOfStations${row.fireDepartmentId}`}>
                    {value ? value : ""}
                </td>,
                "Stations"),
            createFilterableNumberRangeColumnDefinition<IFireDepartment, IFireDepartmentFilters>(
                globalContext,
                "equipment",
                // @ts-ignore
                "equipment",
                true,
                filter,
                (value: any, row: any) => <td className="align-top" key={`equipment${row.fireDepartmentId}`}>{row.equipment?.filter((e: IEquipment) => e.serialNumber?.substring(0, 3).toLocaleLowerCase() != "r01" && e.serialNumber?.substring(0, 3).toLocaleLowerCase() != "d01")?.length || 0}</td>,
                "Equipment"),
            createFilterableNumberRangeColumnDefinition<IFireDepartment, IFireDepartmentFilters>(
                globalContext,
                "healthRiskAssessments",
                // @ts-ignore
                "hras",
                true,
                filter,
                (value: any, row: any) => <td className="align-top" key={`hras${row.fireDepartmentId}`}>{row.healthRiskAssessments?.length || 0}</td>,
                "HRAs"),
            createFilterableNumberRangeColumnDefinition<IFireDepartment, IFireDepartmentFilters>(
                globalContext,
                // @ts-ignore
                "airCards",
                // @ts-ignore
                "airCards",
                true,
                filter,
                (value: any, row: any) => <td className="align-top" key={`airCards${row.fireDepartmentId}`}>{row.equipment?.filter((e: IEquipment) => e.serialNumber?.substring(0, 3).toLocaleLowerCase() == "r01").length}</td>,
                "AirCards"),
            createFilterableArrayColumnDefinition<IFireDepartment, IFireDepartmentFilters>(
                globalContext,
                "website",
                "websites",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`websites${row.fireDepartmentId}`}>
                    {value}</td>,
                "Website")
        ];
    };

    const rowClassRenderer = (row: IFireDepartment) => {
        if (row.equipment?.some(e => e.demoRequestDate && !e.demoStartDate)) {
            return "table-action-required";
        }

        return "";
    };

    const getTools = () => [
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>,
        <>
            {
                (
                    getEnvironment() !== "prod" ||
                    globalContext.userEmail?.toLowerCase() === "erik.ward@tft.com"
                ) &&
                <Button
                    key="createnew"
                    variant="success"
                    className="full-sm"
                    onClick={() => { navigate(`/Departments/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<IFireDepartmentFilters, IFireDepartment, IFireDepartmentCreateOrUpdate>
            baseFilters={{
                sortBys: ["Fdid"],
                sortDescending: true
            }}
            filterHints={{
                fireDepartmentIds: "Array",
                priorities: "Array",
                statuses: "Array",
                actions: "Array",
                fireDeptNames: "Array",
                hqcitys: "Array",
                hqcountries: "Array",
                hqstates: "Array",
                hqzips: "Array",
                mailCitys: "Array",
                mailStates: "Array",
                mailZips: "Array",
                countys: "Array",
                deptTypes: "Array",
                organizationTypes: "Array",
                primaryAgencyForEmergencyMgmt: "Boolean",
                sortBys: "Array",
                sortDescending: "Boolean",
                websites: "Array",
            }}
            dataTypeName="Fire Department"
            dataTypeNamePlural="Fire Departments"
            pathName="Departments"
            primaryIdKey="fireDepartmentId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={FireDepartmentService}
            tools={getTools()}
            rowClassRenderer={rowClassRenderer}
            forceRefresh={forceRefresh}
        />
    );
}

export default FireDepartmentsLookup;
